import {
  ActionIcon,
  Button,
  Card,
  Group,
  LoadingOverlay,
  NumberInput,
  SegmentedControl,
  Select,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core'
import { FaSave } from 'react-icons/fa'
import FocusComparisonCards from './FocusComparisonCards'
import { Rectangle } from './types'
import { RiResetLeftFill } from 'react-icons/ri'
import { useTranslation } from 'react-i18next'

const FocusPanel = ({
  originalImageUrl,
  adjustedImageUrl,
  selectedSquare,
  camera,
  setCamera,
  focusIncrement,
  setFocusIncrement,
  focusValue,
  setFocusValue,
  focusLoading,
  displaySaveButton,
  onSave,
  onResetToInitial,
  initialFocusValue,
  isOriginalLoading,
  isAdjustedLoading,
  onRefetchOriginal,
  onRefetchAdjusted,
}: {
  camera: 'Front Left' | 'Front Center' | 'Front Right'
  setCamera: (value: 'Front Left' | 'Front Center' | 'Front Right') => void
  focusIncrement: number
  setFocusIncrement: (value: number) => void
  focusValue?: number
  setFocusValue: (value: number) => void
  focusLoading: boolean
  displaySaveButton: boolean
  onSave: () => void
  originalImageUrl?: string
  adjustedImageUrl?: string
  selectedSquare?: Rectangle | null
  onSquareChange: (square: Rectangle | null) => void
  isLoading: boolean
  onResetToInitial?: () => void
  initialFocusValue?: number | null
  isOriginalLoading: boolean
  isAdjustedLoading: boolean
  onRefetchOriginal: () => void
  onRefetchAdjusted: () => void
}) => {
  const { t } = useTranslation(['hardware', 'common'])
  return (
    <Tabs.Panel value="focus">
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg">
        <Text mb="xs">{t('selectCamera')}</Text>
        <Select
          mb="md"
          maw={300}
          data={[
            { label: t('frontLeft'), value: 'Front Left' },
            { label: t('frontCenter'), value: 'Front Center' },
            { label: t('frontRight'), value: 'Front Right' },
          ]}
          value={camera}
          onChange={value => value && setCamera(value as 'Front Left' | 'Front Center' | 'Front Right')}
        />
        <Text mb="xs">{t('selectFocusIncrement')}</Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={focusIncrement.toString()}
            onChange={value => setFocusIncrement(parseInt(value))}
            data={[
              { label: '10', value: String(10) },
              { label: '20', value: String(20) },
              { label: '50', value: String(50) },
              { label: '100', value: String(100) },
              { label: '200', value: String(200) },
            ]}
          />
          <NumberInput
            value={focusValue}
            placeholder={t('focusPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={9118}
            step={focusIncrement}
            disabled={focusValue === undefined}
            onChange={value => setFocusValue(Number(value))}
          />
          <LoadingOverlay visible={focusLoading} />
          {onResetToInitial && initialFocusValue !== null && (
            <Tooltip label={t('resetTo', { value: initialFocusValue })} arrowOffset={10} position="top" withArrow>
              <ActionIcon
                size={36}
                variant="default"
                aria-label="Collapse"
                onClick={onResetToInitial}
                disabled={focusValue === initialFocusValue}
              >
                <RiResetLeftFill size={20} />
              </ActionIcon>
            </Tooltip>
          )}
          {displaySaveButton && (
            <Button leftSection={<FaSave />} onClick={onSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <FocusComparisonCards
          originalImageUrl={originalImageUrl}
          adjustedImageUrl={adjustedImageUrl}
          selectedRectangle={selectedSquare}
          isOriginalLoading={isOriginalLoading}
          isAdjustedLoading={isAdjustedLoading}
          onRefetchOriginal={onRefetchOriginal}
          onRefetchAdjusted={onRefetchAdjusted}
        />
      </Card>
    </Tabs.Panel>
  )
}

export default FocusPanel
